"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Loader } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function RequestServiceForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-primary/10 border border-primary/20 rounded-lg">
        <p className="text-foreground-dark text-lg font-medium mb-4">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="grid gap-6 md:grid-cols-2">
        <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Name must be at least 2 characters"
        },
        maxLength: {
          value: 50,
          message: "Name must not exceed 50 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-white/90 font-medium">Your Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} className="bg-white/5 border-white/20 text-white placeholder:text-white/40 focus:border-primary focus:ring-primary/30 transition-all duration-200" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-white/90 font-medium">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" {...field} className="bg-white/5 border-white/20 text-white placeholder:text-white/40 focus:border-primary focus:ring-primary/30 transition-all duration-200" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required",
        pattern: {
          value: /^[0-9\s\-\+\(\)]+$/,
          message: "Please enter a valid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-white/90 font-medium">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="0876 789 903" {...field} className="bg-white/5 border-white/20 text-white placeholder:text-white/40 focus:border-primary focus:ring-primary/30 transition-all duration-200" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="location" rules={{
        required: "Please select a garage location"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-white/90 font-medium">Preferred Garage Location</FormLabel>
              <FormControl>
                <select {...field} className="flex h-9 w-full rounded-md border border-white/20 bg-white/5 px-3 py-1 text-base text-white shadow-xs transition-all outline-none focus:border-primary focus:ring-[3px] focus:ring-primary/30 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm">
                  <option value="" className="bg-dark-section text-white">Select a location</option>
                  <option value="Ballybeg, Rathnew (A67Y602)" className="bg-dark-section text-white">Ballybeg, Rathnew (A67Y602)</option>
                  <option value="Blainroe - Old Vartry Garage" className="bg-dark-section text-white">Blainroe - Old Vartry Garage</option>
                  <option value="New Street, Wicklow Town" className="bg-dark-section text-white">New Street, Wicklow Town</option>
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="vehicle" rules={{
        required: "Vehicle information is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-white/90 font-medium">Vehicle Make & Model</FormLabel>
              <FormControl>
                <Input placeholder="e.g., Toyota Corolla" {...field} className="bg-white/5 border-white/20 text-white placeholder:text-white/40 focus:border-primary focus:ring-primary/30 transition-all duration-200" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="service_type" rules={{
        required: "Please select a service type"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-white/90 font-medium">Type of Service Required</FormLabel>
              <FormControl>
                <select {...field} className="flex h-9 w-full rounded-md border border-white/20 bg-white/5 px-3 py-1 text-base text-white shadow-xs transition-all outline-none focus:border-primary focus:ring-[3px] focus:ring-primary/30 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm">
                  <option value="" className="bg-dark-section text-white">Select service type</option>
                  <option value="Full Diagnostics" className="bg-dark-section text-white">Full Diagnostics</option>
                  <option value="NCT Repair" className="bg-dark-section text-white">NCT Repair</option>
                  <option value="NCT Check" className="bg-dark-section text-white">NCT Check</option>
                  <option value="Tyre / Puncture Repair" className="bg-dark-section text-white">Tyre / Puncture Repair</option>
                  <option value="General Maintenance" className="bg-dark-section text-white">General Maintenance</option>
                  <option value="Other" className="bg-dark-section text-white">Other</option>
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-white/90 font-medium">Additional Details / Message</FormLabel>
            <FormControl>
              <Textarea placeholder="Provide any extra information about the issue..." {...field} className="bg-white/5 border-white/20 text-white placeholder:text-white/40 focus:border-primary focus:ring-primary/30 transition-all duration-200 min-h-[120px]" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
          <p className="text-red-300 text-sm">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto btn-primary">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            Submitting Request...
          </> : 'Request Service'}
      </Button>
    </Form>;
}
export default function RequestAService() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.15,
    once: true
  });
  return <section id="request-a-service" className="relative min-h-[30vh] py-16 md:py-24 bg-dark-section overflow-hidden">
      {}
      <div className="absolute inset-0 bg-cover bg-center bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1537494/img/automotive_workshop_background.png)]" style={{
      transform: 'translateY(calc(var(--scroll-y, 0) * 0.05))',
      filter: 'brightness(0.3)'
    }} />
      
      {}
      <div className="absolute inset-0 bg-black/60" />

      {}
      <div className="container relative z-10">
        <div ref={ref} className={`max-w-4xl mx-auto transition-all duration-200 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
          {}
          <div className="text-center mb-12">
            <h2 className="font-display text-4xl md:text-5xl font-bold text-white mb-4">
              Request a Service
            </h2>
            <p className="text-white/80 text-lg max-w-2xl mx-auto">
              Fill out the form below and our team will get back to you promptly to schedule your vehicle service.
            </p>
          </div>

          {}
          <FormProvider className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-8 md:p-10 shadow-strong" formId="service_request_contact" sectionName="request-a-service" defaultValues={{
          name: "",
          email: "",
          phone: "",
          location: "",
          vehicle: "",
          service_type: "",
          message: ""
        }} validationRules={{
          name: {
            required: true,
            minLength: 2,
            maxLength: 50
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {
            required: true,
            pattern: "phone"
          },
          location: {
            required: true
          },
          vehicle: {
            required: true
          },
          service_type: {
            required: true
          }
        }} submitText="Request Service" successMessage="Thank you for your service request! We'll contact you shortly to confirm your appointment." errorMessage="Failed to submit your request. Please check your information and try again.">
            <RequestServiceForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}